// Screen: "Leaders & Sponsors"
import React, { useState, useEffect } from 'react';
import { getActiveSponsors, getLeaderboardByMetric } from '../apiClient';
import EloBadge, { getRankProgress } from '../components/EloBadge';
import SEO from '../components/SEO';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

const LEADER_CATEGORIES = [
  { key: 'top_earners', label: 'Top Earner', icon: '\uD83D\uDCB0', color: '#d4af37' },
  { key: 'win_rate', label: 'Highest Win Rate', icon: '\uD83C\uDFC6', color: '#3fb950' },
  { key: 'games_played', label: 'Most Games', icon: '\uD83C\uDFAE', color: '#58a6ff' },
  { key: 'longest_streak', label: 'Longest Streak', icon: '\uD83D\uDD25', color: '#f59e0b' },
];

export default function LeaderPage() {
  const isMobile = useIsMobile();
  const [sponsors, setSponsors] = useState([]);
  const [leaders, setLeaders] = useState({});

  useEffect(() => {
    getActiveSponsors().then(data => setSponsors(data.sponsors || []));
    LEADER_CATEGORIES.forEach(cat => {
      getLeaderboardByMetric(cat.key, 'alltime', 1).then(data => {
        if (data && data.length > 0) {
          setLeaders(prev => ({ ...prev, [cat.key]: data[0] }));
        }
      });
    });
  }, []);

  return (
    <>
    <SEO
      title="Leaders & Sponsors"
      description="See the top-ranked Buck Euchre players and sponsors supporting the Euchre Table community."
      path="/leaders"
    />
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '40px 24px',
      maxWidth: 700, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, marginBottom: isMobile ? 16 : 24 }}>Leaders & Sponsors</h1>

      {/* Current Leaders */}
      <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, marginBottom: 12 }}>Current Leaders</h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: 12, marginBottom: 24
      }}>
        {LEADER_CATEGORIES.map(cat => {
          const leader = leaders[cat.key];
          return (
            <div key={cat.key} style={{
              background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
              padding: isMobile ? 14 : 18
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 20 }}>{cat.icon}</span>
                <span style={{ fontSize: 12, color: '#8b949e', fontWeight: 600, textTransform: 'uppercase' }}>
                  {cat.label}
                </span>
              </div>
              {leader ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <EloBadge points={leader.rankPoints || 0} preferredSuit={leader.preferredSuit || '♠'} size="sm" />
                  <div>
                    <div style={{ fontSize: isMobile ? 16 : 20, fontWeight: 700, color: cat.color }}>
                      {leader.nickname || 'Player'}
                    </div>
                    <div style={{ fontSize: 12, color: '#8b949e', marginTop: 2 }}>
                      {cat.key === 'top_earners' ? `${(leader.balance || 0).toLocaleString()} Bucks` :
                       cat.key === 'win_rate' ? `${leader.winRate || 0}% win rate` :
                       cat.key === 'games_played' ? `${leader.gamesPlayed || 0} games` :
                       `${leader.longestStreak || 0} game streak`}
                    </div>
                    {(() => {
                      const p = getRankProgress(leader.rankPoints || 0);
                      return (
                        <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>
                          {p.nextRank ? `${p.pointsToNext} pts to ${p.nextRank.name}` : 'Max Rank'}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <div style={{ color: '#484f58', fontSize: 12 }}>No data yet</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Sponsors */}
      <h2 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, marginBottom: 12 }}>Sponsors</h2>
      {sponsors.length === 0 ? (
        <div style={{
          background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
          padding: 40, textAlign: 'center'
        }}>
          <div style={{ color: '#8b949e', fontSize: 16, marginBottom: 8 }}>No sponsors yet</div>
          <div style={{ color: '#484f58', fontSize: 13 }}>
            Interested in sponsoring? Contact us at <span style={{ color: '#58a6ff' }}>sponsors@euchretable.com</span>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sponsors.map(s => (
            <div key={s.sponsor_id} style={{
              background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
              padding: isMobile ? 14 : 18
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {s.logo_url && (
                  <img src={s.logo_url} alt={s.name} style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'contain' }} />
                )}
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: '#8b949e', textTransform: 'uppercase' }}>{s.tier} Sponsor</div>
                </div>
                {s.website_url && (
                  <a href={s.website_url} target="_blank" rel="noopener noreferrer" style={{
                    marginLeft: 'auto', color: '#58a6ff', fontSize: 12, textDecoration: 'none'
                  }}>Visit</a>
                )}
              </div>
              {s.prizes && s.prizes.length > 0 && (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #21262d' }}>
                  {s.prizes.map(p => (
                    <div key={p.prize_id} style={{ fontSize: 12, color: '#c9d1d9', marginBottom: 4 }}>
                      <span style={{ color: '#d4af37' }}>&#9733;</span> {p.prize_description}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
    </>
  );
}
